import { useState, useEffect, ReactNode } from 'react';
import { AuthContext, User } from './AuthContext';

const BYPASS_FAVORITES_KEY = 'hub_dev_favorites';
const isBypassMode = import.meta.env.DEV && import.meta.env.VITE_BYPASS_HUB_AUTH === 'true';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authorizedApps, setAuthorizedApps] = useState<string[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);

  // Vérifier la session au chargement
  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    // Bypass auth en dev pour permettre le travail en local sans Netlify functions
    if (isBypassMode) {
      setUser({
        id: 'dev-user',
        email: 'dev@local',
        nom: 'Dev',
        prenom: 'User',
        role: 'superviseur',
      });
      setAuthorizedApps(['direction', 'gop', 'cal', 'aas', 'formations', 'crah', 'terrasses', 'maraude', 'cri', 'outilsmanager', 'hub_maincourante', 'pvmanagersiret', 'etablissementspvmanager', 'affichages', 'conseildequartier', 'chantale', 'elmepm', 'hs', 'chefdebrigade', 'parisdata', 'jardinsparis']);
      try {
        const stored = localStorage.getItem(BYPASS_FAVORITES_KEY);
        setFavorites(stored ? JSON.parse(stored) : []);
      } catch {
        setFavorites([]);
      }
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth-verify', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setAuthorizedApps(data.authorizedApps || []);
        fetchFavorites();
      }
    } catch (error) {
      console.error('Session check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await fetch('/api/favorites-get', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error('Fetch favorites failed:', error);
    }
  };

  const toggleFavorite = async (appSlug: string) => {
    const wasFavorite = favorites.includes(appSlug);
    const next = wasFavorite ? favorites.filter((s) => s !== appSlug) : [...favorites, appSlug];
    setFavorites(next);

    // En bypass dev, pas de backend disponible : on persiste en local uniquement
    if (isBypassMode) {
      localStorage.setItem(BYPASS_FAVORITES_KEY, JSON.stringify(next));
      return;
    }

    try {
      const response = await fetch('/api/favorites-toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ appSlug }),
      });

      if (!response.ok) {
        setFavorites((prev) =>
          wasFavorite ? [...prev, appSlug] : prev.filter((s) => s !== appSlug)
        );
      }
    } catch (error) {
      console.error('Toggle favorite failed:', error);
      setFavorites((prev) =>
        wasFavorite ? [...prev, appSlug] : prev.filter((s) => s !== appSlug)
      );
    }
  };

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      const response = await fetch('/api/auth-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, rememberMe }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Erreur de connexion' };
      }

      if (data.mustChangePassword) {
        return { success: true, mustChangePassword: true };
      }

      setUser(data.user);
      setAuthorizedApps(data.authorizedApps || []);
      fetchFavorites();
      return { success: true, mustChangePassword: false };
    } catch (error) {
      return { success: false, error: 'Erreur réseau' };
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/auth-logout', {
        method: 'POST',
        credentials: 'include',
      });
      setUser(null);
      setAuthorizedApps([]);
      setFavorites([]);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const changePassword = async (newPassword: string) => {
    try {
      const response = await fetch('/api/auth-change-pwd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Erreur lors du changement de mot de passe' };
      }

      // Recharger la session après changement de mdp
      await checkSession();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Erreur réseau' };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, changePassword, authorizedApps, favorites, toggleFavorite }}>
      {children}
    </AuthContext.Provider>
  );
}
